// A17 v2: thread surface inside the right drawer. Lives in the
// canonical drawer slot per spec — clicking a thread anywhere in the
// workspace opens it here. Captain pivot 2026-04-23: "the right
// drawer is our canon place where things open when content is
// clicked now."

import { useEffect, useMemo, useState } from "react";
import {
  ApiError,
  decodeIdentity,
  extractRows,
  listThreadMessages,
  postThreadMessage,
} from "../api/client";

interface Props {
  threadId: number;
  title: string;
  threadKind: "group" | "dm";
}

interface ThreadMessage {
  thread_message_id: number;
  author_kind: string;
  author_identity: string;
  author_agent_id: number;
  body: string;
  created_at: number;
}

export function ThreadDrawerView({ threadId, title, threadKind }: Props) {
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const result = await listThreadMessages(threadId);
      const parsed: ThreadMessage[] = extractRows(result).map((r) => ({
        thread_message_id: Number(r[0] ?? 0),
        author_kind: String(r[1] ?? ""),
        author_identity: decodeIdentity(r[2]),
        author_agent_id: Number(r[3] ?? 0),
        body: String(r[4] ?? ""),
        created_at: Number(r[5] ?? 0),
      }));
      setMessages(parsed);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  const submit = async () => {
    if (!body.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await postThreadMessage(threadId, body.trim());
      setBody("");
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const sortedMessages = useMemo(
    () => [...messages].sort((a, b) => a.created_at - b.created_at),
    [messages],
  );

  return (
    <div
      className="flex h-full flex-col gap-2 px-3 py-3"
      data-testid={`drawer-thread-${threadId}`}
    >
      <header className="border-b border-edge pb-1">
        <p className="text-xs font-medium text-primary">{title}</p>
        <p className="font-code text-[0.625rem] text-faint">
          {threadKind} · thread {threadId}
        </p>
      </header>
      <ul className="flex-1 overflow-auto">
        {sortedMessages.length === 0 ? (
          <p className="text-[0.625rem] text-faint">No messages yet.</p>
        ) : (
          sortedMessages.map((m) => (
            <li
              key={m.thread_message_id}
              className="mb-1 rounded border border-edge bg-surface px-2 py-1 text-xs text-primary"
            >
              <span className="font-code text-[0.625rem] text-faint">
                {m.author_kind} ·{" "}
                {new Date(m.created_at / 1000).toLocaleTimeString()}
              </span>
              <p className="mt-0.5 whitespace-pre-wrap">{m.body}</p>
            </li>
          ))
        )}
      </ul>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void submit();
            }
          }}
          placeholder="Reply…"
          className="flex-1 rounded-md border border-edge bg-surface px-2 py-1 text-xs text-primary placeholder:text-faint"
        />
        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy || !body.trim()}
          className="rounded-md border border-accent px-2 py-1 text-[0.625rem] font-medium text-accent hover:bg-accent/10 disabled:opacity-50"
        >
          Send
        </button>
      </div>
      {error && <p className="text-[0.625rem] text-danger">{error}</p>}
    </div>
  );
}
